import React, { useState, useEffect } from 'react';
import {
  ShieldAlert,
  ShieldCheck,
  Pause,
  Play,
  Trash2,
  Lock,
  AlertTriangle,
  CheckCircle2,
  Info,
  RotateCcw,
  Clock,
  Sparkles,
  Zap,
  ArrowRight,
  ChevronRight,
  X,
  Check
} from 'lucide-react';
import {
  getCompanionProfileStatus,
  pauseProfile,
  resumeProfile,
  requestDeleteAccount,
  ProfileStatusResponse,
} from '../../lib/companionProfileService';
import {
  CompanionProfileStatus,
  CompanionEligibility,
} from '../../types/companionProfile.types';
import { apiFetch as fetch } from '../../lib/apiConfig';

interface CompanionLifecycleManagerProps {
  userId?: string;
  onStatusChange?: (newStatus: CompanionProfileStatus) => void;
}

export const CompanionLifecycleManager: React.FC<CompanionLifecycleManagerProps> = ({
  userId,
  onStatusChange,
}) => {
  const [profileStatus, setProfileStatus] = useState<CompanionProfileStatus>('ACTIVE');
  const [eligibility, setEligibility] = useState<CompanionEligibility>({
    hasBookingHistory: false,
    hasActiveBooking: false,
    hasUnresolvedPayment: false,
    canPause: true,
    canResume: false,
    canDelete: true,
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Pause Reason Modal
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');

  // Delete Confirm Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Acceptance Test Bench State (Section 24)
  const [testLog, setTestLog] = useState<{ testName: string; status: 'PASS' | 'FAIL' | 'RUNNING'; details: string; timestamp: string }[]>([]);
  const [isTesting, setIsTesting] = useState(false);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 5000);
  };

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await getCompanionProfileStatus(userId);
      if (res.success && res.profile) {
        setProfileStatus(res.profile.status);
      }
      if (res.eligibility) {
        setEligibility(res.eligibility);
      }
    } catch (err: any) {
      showToast('Error loading profile status: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, [userId]);

  // Handle Pause
  const handlePause = async () => {
    setActionLoading(true);
    try {
      const res = await pauseProfile(pauseReason || 'Companion requested temporary pause', userId);
      if (res.success) {
        setProfileStatus('PAUSED');
        showToast(res.message || 'Profile paused successfully.', 'success');
        setShowPauseModal(false);
        setPauseReason('');
        loadStatus();
        if (onStatusChange) onStatusChange('PAUSED');
      } else {
        showToast(res.message || 'Failed to pause profile', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to pause profile', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Resume
  const handleResume = async () => {
    setActionLoading(true);
    try {
      const res = await resumeProfile(userId);
      if (res.success) {
        setProfileStatus('ACTIVE');
        showToast(res.message || 'Profile resumed successfully.', 'success');
        loadStatus();
        if (onStatusChange) onStatusChange('ACTIVE');
      } else {
        showToast(res.message || 'Failed to resume profile', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to resume profile', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Delete Account Request
  const handleDelete = async () => {
    setActionLoading(true);
    try {
      const res = await requestDeleteAccount(userId);
      if (res.success) {
        setProfileStatus('DEACTIVATED');
        showToast(res.message || 'Account deletion request submitted.', 'info');
        setShowDeleteModal(false);
        loadStatus();
        if (onStatusChange) onStatusChange('DEACTIVATED');
      } else {
        showToast(res.message || 'Failed to submit delete request', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to request delete', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // =========================================================================
  // Section 24: Final Acceptance Test Bench Runner (Tests 1 through 7)
  // =========================================================================
  const runAcceptanceTest = async (testId: number) => {
    setIsTesting(true);
    const targetUserId = `test-comp-${Date.now()}`;
    const timestamp = new Date().toLocaleTimeString();

    try {
      if (testId === 1) {
        // Test 1: New Companion (0 Bookings, Active) -> Pause = ENABLED, Delete = ENABLED
        const res = await getCompanionProfileStatus(targetUserId);
        const passed = res.eligibility.canPause && res.eligibility.canDelete && !res.eligibility.hasBookingHistory;
        setTestLog((prev) => [
          {
            testName: 'Test 1 — New Companion (0 Bookings)',
            status: passed ? 'PASS' : 'FAIL',
            details: `canPause=${res.eligibility.canPause}, canDelete=${res.eligibility.canDelete}, hasBookingHistory=${res.eligibility.hasBookingHistory}`,
            timestamp,
          },
          ...prev,
        ]);
        showToast(passed ? 'Test 1 PASSED: New Companion has Pause & Delete ENABLED.' : 'Test 1 FAILED', passed ? 'success' : 'error');
      } else if (testId === 2) {
        // Test 2: Previous Completed Booking -> Pause = BLOCKED, Delete = BLOCKED
        await fetch(`/api/test/companion-lifecycle/seed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: targetUserId, bookingStatus: 'COMPLETED' }),
        });
        const res = await getCompanionProfileStatus(targetUserId);
        const passed = !res.eligibility.canPause && !res.eligibility.canDelete && res.eligibility.hasBookingHistory;
        setTestLog((prev) => [
          {
            testName: 'Test 2 — Previous Completed Booking',
            status: passed ? 'PASS' : 'FAIL',
            details: `hasBookingHistory=${res.eligibility.hasBookingHistory}, canPause=${res.eligibility.canPause} (BLOCKED), canDelete=${res.eligibility.canDelete} (BLOCKED)`,
            timestamp,
          },
          ...prev,
        ]);
        showToast(passed ? 'Test 2 PASSED: Completed booking history strictly blocks Pause & Delete.' : 'Test 2 FAILED', passed ? 'success' : 'error');
      } else if (testId === 3) {
        // Test 3: Cancelled Booking -> Pause = BLOCKED, Delete = BLOCKED
        await fetch(`/api/test/companion-lifecycle/seed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: targetUserId, bookingStatus: 'CANCELLED' }),
        });
        const res = await getCompanionProfileStatus(targetUserId);
        const passed = !res.eligibility.canPause && !res.eligibility.canDelete && res.eligibility.hasBookingHistory;
        setTestLog((prev) => [
          {
            testName: 'Test 3 — Cancelled Booking',
            status: passed ? 'PASS' : 'FAIL',
            details: `hasBookingHistory=${res.eligibility.hasBookingHistory}, canPause=${res.eligibility.canPause} (BLOCKED), canDelete=${res.eligibility.canDelete} (BLOCKED)`,
            timestamp,
          },
          ...prev,
        ]);
        showToast(passed ? 'Test 3 PASSED: Cancelled booking retained in history blocks Pause & Delete.' : 'Test 3 FAILED', passed ? 'success' : 'error');
      } else if (testId === 4) {
        // Test 4: Active Booking (Status = CONFIRMED) -> Pause = BLOCKED, Delete = BLOCKED
        await fetch(`/api/test/companion-lifecycle/seed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: targetUserId, bookingStatus: 'CONFIRMED' }),
        });
        const res = await getCompanionProfileStatus(targetUserId);
        const passed = res.eligibility.hasActiveBooking && !res.eligibility.canPause && !res.eligibility.canDelete;
        setTestLog((prev) => [
          {
            testName: 'Test 4 — Active Booking (CONFIRMED)',
            status: passed ? 'PASS' : 'FAIL',
            details: `hasActiveBooking=${res.eligibility.hasActiveBooking}, canPause=${res.eligibility.canPause} (BLOCKED), canDelete=${res.eligibility.canDelete} (BLOCKED)`,
            timestamp,
          },
          ...prev,
        ]);
        showToast(passed ? 'Test 4 PASSED: Active booking blocks Pause & Delete.' : 'Test 4 FAILED', passed ? 'success' : 'error');
      } else if (testId === 5) {
        // Test 5: Direct API Attack (Attempt POST /api/companion/profile/pause with booking history)
        // Must return HTTP 409 Conflict with PROFILE_PAUSE_BLOCKED_BOOKING_HISTORY
        await fetch(`/api/test/companion-lifecycle/seed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: targetUserId, bookingStatus: 'COMPLETED' }),
        });
        const rawRes = await fetch('/api/companion/profile/pause', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-user-id': targetUserId },
          body: JSON.stringify({ pauseReason: 'Malicious direct API call bypass' }),
        });
        const data = await rawRes.json();
        const passed = rawRes.status === 409 && data.code === 'PROFILE_PAUSE_BLOCKED_BOOKING_HISTORY';
        setTestLog((prev) => [
          {
            testName: 'Test 5 — Direct API Bypass Attack',
            status: passed ? 'PASS' : 'FAIL',
            details: `HTTP ${rawRes.status} Conflict | Code: ${data.code} | Message: "${data.message}"`,
            timestamp,
          },
          ...prev,
        ]);
        showToast(passed ? 'Test 5 PASSED: Server rejected bypass with 409 Conflict.' : 'Test 5 FAILED', passed ? 'success' : 'error');
      } else if (testId === 6) {
        // Test 6: Resume Profile (Profile = PAUSED, 0 bookings -> Resume = ENABLED)
        await pauseProfile('Test pause', targetUserId);
        const resumeRes = await resumeProfile(targetUserId);
        const passed = resumeRes.success && resumeRes.profileStatus === 'ACTIVE';
        setTestLog((prev) => [
          {
            testName: 'Test 6 — Resume Profile from PAUSED',
            status: passed ? 'PASS' : 'FAIL',
            details: `resumeResult: success=${resumeRes.success}, status=${resumeRes.profileStatus}`,
            timestamp,
          },
          ...prev,
        ]);
        showToast(passed ? 'Test 6 PASSED: Resume Profile succeeded.' : 'Test 6 FAILED', passed ? 'success' : 'error');
      } else if (testId === 7) {
        // Test 7: Booking Creation Protection (Profile = PAUSED -> New Booking BLOCKED with COMPANION_NOT_AVAILABLE)
        await pauseProfile('Temporarily off-duty', targetUserId);
        const bookingRes = await fetch('/api/pairings/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: 'client_buyer_test',
            companionId: targetUserId,
            serviceType: 'Dining',
            totalFee: 2000,
          }),
        });
        const data = await bookingRes.json();
        const passed = bookingRes.status === 409 && data.code === 'COMPANION_NOT_AVAILABLE';
        setTestLog((prev) => [
          {
            testName: 'Test 7 — Booking Creation After Pause',
            status: passed ? 'PASS' : 'FAIL',
            details: `HTTP ${bookingRes.status} | Code: ${data.code} | Message: "${data.message}"`,
            timestamp,
          },
          ...prev,
        ]);
        showToast(passed ? 'Test 7 PASSED: Booking blocked when companion is PAUSED.' : 'Test 7 FAILED', passed ? 'success' : 'error');
      }
    } catch (err: any) {
      showToast('Test execution error: ' + err.message, 'error');
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Notice */}
      {feedback && (
        <div
          className={`p-3.5 rounded-2xl text-xs font-bold flex items-center justify-between border transition-all ${
            feedback.type === 'success'
              ? 'bg-emerald-950/90 border-emerald-800 text-emerald-200'
              : feedback.type === 'error'
              ? 'bg-rose-950/90 border-rose-800 text-rose-200'
              : 'bg-purple-950/90 border-purple-800 text-purple-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {feedback.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-rose-400" />
            )}
            <span>{feedback.message}</span>
          </div>
          <button onClick={() => setFeedback(null)} className="p-1 hover:text-white cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SECTION 18 SPECIFICATION MATCH: Profile Status & Action Cards             */}
      {/* ========================================================================= */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl relative overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-purple-400 uppercase tracking-widest">
                PAIRUPTODAY LIFECYCLE GOVERNANCE
              </span>
              <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-[10px] font-mono font-bold">
                PostgreSQL ON DELETE RESTRICT
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight mt-1">
              Companion Profile Status & Account Lifecycle
            </h2>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl leading-relaxed">
              Companion lifecycle status controls your availability in public search and client bookings.
              Once booking history exists, profile pause and self-deletion are strictly protected server-side.
            </p>
          </div>

          <button
            onClick={loadStatus}
            disabled={loading}
            className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer self-start sm:self-auto"
            title="Refresh Lifecycle Status"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-purple-400' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Status Indicator (Section 18 exact mockup visual) */}
        <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800/80 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono font-bold text-slate-400 uppercase tracking-wider">
              Profile Status
            </span>
            <div className="flex items-center gap-2">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  profileStatus === 'ACTIVE'
                    ? 'bg-emerald-400 animate-pulse'
                    : profileStatus === 'PAUSED'
                    ? 'bg-amber-400'
                    : 'bg-rose-500'
                }`}
              />
              <span
                className={`font-mono text-xs font-black uppercase tracking-wider ${
                  profileStatus === 'ACTIVE'
                    ? 'text-emerald-400'
                    : profileStatus === 'PAUSED'
                    ? 'text-amber-400'
                    : 'text-rose-400'
                }`}
              >
                ● {profileStatus}
              </span>
            </div>
          </div>

          <div className="h-px bg-slate-800/80" />

          {/* Action Buttons & Blocked UI Warnings (Section 17 & 18 Match) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
            {/* 1. Pause Profile Button */}
            {profileStatus === 'ACTIVE' ? (
              <div className="space-y-2">
                <button
                  disabled={!eligibility.canPause || actionLoading}
                  onClick={() => setShowPauseModal(true)}
                  className={`w-full py-3 px-4 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all cursor-pointer ${
                    eligibility.canPause
                      ? 'bg-amber-600 hover:bg-amber-500 text-white shadow-md shadow-amber-950'
                      : 'bg-slate-800/80 text-slate-500 border border-slate-700/60 cursor-not-allowed opacity-60'
                  }`}
                >
                  <Pause className="w-3.5 h-3.5" />
                  <span>Pause Profile</span>
                  {!eligibility.canPause && <Lock className="w-3.5 h-3.5 text-amber-400" />}
                </button>
                {!eligibility.canPause && (
                  <p className="text-[11px] text-amber-400/90 leading-tight">
                    {eligibility.hasBookingHistory
                      ? 'You cannot pause your profile because your account has booking history.'
                      : eligibility.hasActiveBooking
                      ? 'You cannot pause your profile while you have an active booking.'
                      : 'You cannot pause your profile while there is an unresolved payment or payout.'}
                  </p>
                )}
              </div>
            ) : (
              /* 2. Resume Profile Button */
              <div className="space-y-2">
                <button
                  disabled={!eligibility.canResume || actionLoading}
                  onClick={handleResume}
                  className={`w-full py-3 px-4 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all cursor-pointer ${
                    eligibility.canResume
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-md shadow-emerald-950'
                      : 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-60'
                  }`}
                >
                  <Play className="w-3.5 h-3.5" />
                  <span>Resume Profile</span>
                </button>
                <p className="text-[11px] text-emerald-400/90 leading-tight">
                  Click to reactivate public availability and start receiving booking requests.
                </p>
              </div>
            )}

            {/* 3. Delete Account Button */}
            <div className="space-y-2 sm:col-span-2">
              <button
                disabled={!eligibility.canDelete || actionLoading}
                onClick={() => setShowDeleteModal(true)}
                className={`w-full py-3 px-4 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all cursor-pointer ${
                  eligibility.canDelete
                    ? 'bg-rose-950 hover:bg-rose-900 border border-rose-800 text-rose-200'
                    : 'bg-slate-800/80 text-slate-500 border border-slate-700/60 cursor-not-allowed opacity-60'
                }`}
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete Account</span>
                {!eligibility.canDelete && <Lock className="w-3.5 h-3.5 text-rose-400" />}
              </button>
              {!eligibility.canDelete && (
                <p className="text-[11px] text-rose-400/90 leading-tight">
                  {eligibility.hasBookingHistory
                    ? 'Account deletion is unavailable because your account has booking history.'
                    : eligibility.hasActiveBooking
                    ? 'Account deletion is unavailable while you have an active booking.'
                    : 'Account deletion is unavailable while there is an unresolved payment.'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Backend Eligibility Telemetry Card */}
        <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 text-xs font-mono space-y-2">
          <div className="flex items-center justify-between text-slate-400 text-[11px]">
            <span>SERVER-VERIFIED ELIGIBILITY CHECK</span>
            <span className="text-purple-400">Database Ground Truth</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800/80">
              <span className="text-[10px] text-slate-500 block">Booking History:</span>
              <strong className={eligibility.hasBookingHistory ? 'text-amber-400' : 'text-slate-300'}>
                {eligibility.hasBookingHistory ? 'FOUND (> 0 records)' : 'NONE (0 records)'}
              </strong>
            </div>
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800/80">
              <span className="text-[10px] text-slate-500 block">Active Bookings:</span>
              <strong className={eligibility.hasActiveBooking ? 'text-rose-400' : 'text-slate-300'}>
                {eligibility.hasActiveBooking ? 'YES (Pending/Confirmed)' : 'NO'}
              </strong>
            </div>
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800/80">
              <span className="text-[10px] text-slate-500 block">Pending Payments:</span>
              <strong className={eligibility.hasUnresolvedPayment ? 'text-rose-400' : 'text-slate-300'}>
                {eligibility.hasUnresolvedPayment ? 'YES (Hold/Escrow)' : 'NONE'}
              </strong>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* SECTION 24: FINAL ACCEPTANCE TESTS BENCH (Tests 1 through 7)             */}
        {/* ========================================================================= */}
        <div className="p-6 rounded-2xl bg-purple-950/20 border border-purple-900/50 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-400" />
              <h3 className="text-sm font-black text-white">
                Final Acceptance Tests Suite (Specification Section 24)
              </h3>
            </div>
            <span className="text-[10px] font-mono text-purple-300">Automated Spec Verification</span>
          </div>
          <p className="text-xs text-slate-300">
            Click any acceptance test scenario below to execute real backend database validations 
            and confirm exact error codes (`409 Conflict`, `ON DELETE RESTRICT`, `COMPANION_NOT_AVAILABLE`).
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <button
              onClick={() => runAcceptanceTest(1)}
              disabled={isTesting}
              className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-left text-xs font-bold text-slate-200 transition-all cursor-pointer hover:border-purple-500"
            >
              <div className="text-[10px] text-purple-400 font-mono">TEST 1</div>
              <div>New Companion (0 Bookings)</div>
              <div className="text-[10px] text-emerald-400 font-normal">Expect: Pause & Delete ENABLED</div>
            </button>

            <button
              onClick={() => runAcceptanceTest(2)}
              disabled={isTesting}
              className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-left text-xs font-bold text-slate-200 transition-all cursor-pointer hover:border-purple-500"
            >
              <div className="text-[10px] text-purple-400 font-mono">TEST 2</div>
              <div>Completed Booking History</div>
              <div className="text-[10px] text-rose-400 font-normal">Expect: Pause & Delete BLOCKED</div>
            </button>

            <button
              onClick={() => runAcceptanceTest(3)}
              disabled={isTesting}
              className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-left text-xs font-bold text-slate-200 transition-all cursor-pointer hover:border-purple-500"
            >
              <div className="text-[10px] text-purple-400 font-mono">TEST 3</div>
              <div>Cancelled Booking History</div>
              <div className="text-[10px] text-rose-400 font-normal">Expect: Retained & BLOCKED</div>
            </button>

            <button
              onClick={() => runAcceptanceTest(4)}
              disabled={isTesting}
              className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-left text-xs font-bold text-slate-200 transition-all cursor-pointer hover:border-purple-500"
            >
              <div className="text-[10px] text-purple-400 font-mono">TEST 4</div>
              <div>Active Confirmed Booking</div>
              <div className="text-[10px] text-rose-400 font-normal">Expect: Pause & Delete BLOCKED</div>
            </button>

            <button
              onClick={() => runAcceptanceTest(5)}
              disabled={isTesting}
              className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-left text-xs font-bold text-slate-200 transition-all cursor-pointer hover:border-purple-500"
            >
              <div className="text-[10px] text-rose-400 font-mono">TEST 5 (SECURITY ATTACK)</div>
              <div>Direct API Bypass Attack</div>
              <div className="text-[10px] text-rose-400 font-normal">Expect: HTTP 409 Conflict</div>
            </button>

            <button
              onClick={() => runAcceptanceTest(6)}
              disabled={isTesting}
              className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-left text-xs font-bold text-slate-200 transition-all cursor-pointer hover:border-purple-500"
            >
              <div className="text-[10px] text-purple-400 font-mono">TEST 6</div>
              <div>Resume from Paused</div>
              <div className="text-[10px] text-emerald-400 font-normal">Expect: Status ACTIVE</div>
            </button>

            <button
              onClick={() => runAcceptanceTest(7)}
              disabled={isTesting}
              className="p-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-left text-xs font-bold text-slate-200 transition-all cursor-pointer hover:border-purple-500"
            >
              <div className="text-[10px] text-purple-400 font-mono">TEST 7</div>
              <div>Booking Creation After Pause</div>
              <div className="text-[10px] text-rose-400 font-normal">Expect: COMPANION_NOT_AVAILABLE</div>
            </button>
          </div>

          {/* Test Log Stream */}
          {testLog.length > 0 && (
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-xs font-mono space-y-1.5 max-h-48 overflow-y-auto">
              {testLog.map((log, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={log.status === 'PASS' ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>
                    [{log.status}]
                  </span>
                  <span className="text-purple-300">{log.testName}:</span>
                  <span className="text-slate-300">{log.details}</span>
                  <span className="text-slate-500 ml-auto">{log.timestamp}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* PAUSE REASON MODAL */}
      {showPauseModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-purple-900/60 rounded-3xl max-w-md w-full p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Pause className="w-4 h-4 text-amber-400" />
                <span>Pause Companion Profile</span>
              </h3>
              <button onClick={() => setShowPauseModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              Pausing your profile temporarily hides your card from public discover. 
              Clients will not be able to send new booking requests until you resume.
            </p>
            <div>
              <label className="text-xs font-bold text-slate-400 block mb-1">Pause Reason (Optional)</label>
              <textarea
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="e.g. Traveling on vacation, taking personal time off..."
                className="w-full h-20 p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setShowPauseModal(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handlePause}
                disabled={actionLoading}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-bold cursor-pointer"
              >
                {actionLoading ? 'Pausing...' : 'Confirm Pause'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE REASON CONFIRM MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-rose-900/60 rounded-3xl max-w-md w-full p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-rose-400" />
                <span>Request Account Deletion</span>
              </h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              Are you sure you want to request account deletion? 
              This operation is only permissible because your account has zero booking history.
            </p>
            <div className="p-3 bg-rose-950/40 border border-rose-900/60 rounded-xl text-[11px] text-rose-300">
              Notice: Once deactivated, your profile listing and verification will be permanently archived.
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold cursor-pointer"
              >
                {actionLoading ? 'Processing...' : 'Confirm Deletion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
